import classNames from 'classnames'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { useTracking } from 'lib/analytics'
import { InfoButton } from 'components/buttons'
import { CardsSection as CardsSectionType } from '../../LandingPage.types'
import css from './CardsSection.styles.scss'
import Card from './components/Card/Card'

interface CardsSectionProps {
  section: CardsSectionType
}

// eslint-disable-next-line complexity
const CardsSection = ({
  section: {
    background,
    headerText,
    subHeaderText,
    imageShape,
    cards,
    mobileVariant,
    desktopVariant,
    desktopWidth,
    callToAction,
  },
}: CardsSectionProps) => {
  const [currentUser] = useCurrentUser()
  const tracker = useTracking()
  const theme = useTheme()
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const useMobileListView = isExtraSmallScreen && mobileVariant === 'list'
  const useListView =
    (!isExtraSmallScreen && isSmallScreen && imageShape !== 'largeRectangle') ||
    useMobileListView
  const cardImageShape = useMobileListView ? 'smallSquare' : imageShape

  let gapClass: string | null = null
  if (useListView) {
    gapClass = css.CardsSection__listView
  } else if (imageShape === 'largeRectangle') {
    gapClass = css.CardsSection__largeImageCardGap
  } else if (desktopVariant === 'twoColumns') {
    gapClass = css.CardsSection__smallCardGap
  } else {
    gapClass = css.CardsSection__largeCardGap
  }
  let columnClass = css['CardsSection__cardContainer--oneColumn']
  if (!isSmallScreen) {
    columnClass =
      desktopVariant === 'twoColumns'
        ? css['CardsSection__cardContainer--twoColumns']
        : css['CardsSection__cardContainer--threeColumns']
  }
  const hasHeaderContent = headerText || subHeaderText

  const renderHeader = () => {
    if (!hasHeaderContent) {
      return null
    }

    return (
      <div className={css.CardsSection__headerContainer}>
        {headerText && (
          <h3 className={css.CardsSection__headerText}>{headerText}</h3>
        )}
        {subHeaderText && (
          <h4 className={css.CardsSection__subHeaderText}>{subHeaderText}</h4>
        )}
      </div>
    )
  }

  const renderCallToAction = () => {
    if (!callToAction) return null
    if (callToAction.type === 'custom_component') {
      return callToAction.component
    }
    return (
      <InfoButton
        pill
        className={css.CardsSection__callToAction}
        href={callToAction.url}
        onClick={() => {
          if (typeof callToAction.onClick === 'function') {
            callToAction.onClick(currentUser, tracker)
          }
        }}
      >
        {callToAction.text}
      </InfoButton>
    )
  }

  return (
    <div
      className={classNames(css.CardsSection__container, {
        [css['CardsSection__container--fullWidth']]: desktopWidth === 'full',
        [css['CardsSection__container--creamBackground']]:
          background === 'cream',
      })}
    >
      {renderHeader()}
      <div
        className={classNames(
          css.CardsSection__cardContainer,
          columnClass,
          gapClass
        )}
      >
        {cards.map((card) => (
          <Card
            card={card}
            desktopVariant={desktopVariant}
            imageShape={cardImageShape}
            key={card.headerText}
            listView={useListView}
          />
        ))}
      </div>
      {renderCallToAction()}
    </div>
  )
}

export default CardsSection
